// Central catalog of backend paths. Every SWR hook and every mutation in
// `services/api.js` should resolve URLs through this module — *not* by
// inlining string literals — so a backend rename only requires editing
// one file instead of grepping every consumer.
//
// Also: SWR uses the URL as the cache key. Two hooks calling the same
// endpoint via API.devices.myDevices share a single cache entry / single
// network request automatically. Two hooks each interpolating their own
// `${API_URL}/devices/my-devices` literal would *also* share a key — but
// the moment one of them drifts (a missing slash, a trailing `?`, a typo)
// the cache splits. Centralizing prevents that whole category of bug.
//
// Verified against:
//   phenodeX/docs/frontend-backend-api.md
//   phenodeX/phenode_backend/api/*/routes.py

// Best-effort IANA timezone of the user's browser (e.g. "America/Chicago"),
// sent as `?tz=` on CSV download requests so the backend can localize the CSV
// timestamps to the viewer when the PheNode's own timezone is unknown
// (ADR-0001 rung 4). Empty string when the browser can't report one.
function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

// Append `tz=<browser zone>` to a download URL, choosing ? or & correctly.
// No-op when the browser reports no zone. Harmless before the backend declares
// the param — FastAPI ignores unknown query params.
function withTz(path) {
  const tz = browserTimeZone();
  if (!tz) return path;
  return `${path}${path.includes('?') ? '&' : '?'}tz=${encodeURIComponent(tz)}`;
}

const API = {
  auth: {
    login: '/auth/login',
    refresh: '/auth/refresh',
    googleLogin: '/auth/google/login',
    token: '/auth/token',
    // PUT — current user changes (or sets) their own password.
    // Body: { current_password?, new_password }. current_password is
    // required if the user already has a password hash; optional for
    // Google-only accounts setting one for the first time.
    // Source: phenodeX/docs/frontend-backend-api.md:340-372
    password: '/auth/password',
    // POST, no auth — emails a reset link. Body: { email }. Always 202 with
    // the same message, whether or not the email has an account.
    forgotPassword: '/auth/password/forgot',
    // POST, no auth — Body: { token, new_password }. 400 when the link is
    // invalid, used, or expired.
    resetPassword: '/auth/password/reset'
  },
  devices: {
    // GET — DeviceRead[] visible to current user.
    // Source: phenodeX/phenode_backend/api/devices/routes.py:38
    myDevices: '/devices/my-devices',
    // PUT — update device label. Path param is the external_device_id,
    // not the numeric DB id. Source: same file, see frontend-backend-api.md:348-360.
    update: (externalDeviceId) => `/devices/${externalDeviceId}`,
    // POST — hide the device from fleet cards + maps (GLOBAL flag, one-way:
    //   an owner (not a viewer) can hide; restore is admin-only via
    //   API.admin.deviceVisibility). Source:
    //   phenodeX/phenode_backend/api/devices/routes.py (hide_device).
    hide: (externalDeviceId) => `/devices/${externalDeviceId}/hide`,
    // GET — typed JSON time-series for one device. Required query params
    // `from` / `to` (ISO-8601). Optional `fields` (CSV string), `limit`
    // (default 10000, max 100000), `bucket` ('raw' | '5m' | '10m' | '15m' |
    // '30m' | '1h' | '3h' | '6h' | '12h' | '1d' | 'auto').
    //
    // Response shape:
    //   { deviceExternalId, from, to, rows: [...] }
    //
    // Row shape depends on bucket:
    //   raw     → { time, latitude, longitude, temperature, humidity, ... }
    //   bucketed → { time, temperature_min, temperature_max, temperature_avg, ... }
    //
    // Source: phenodeX/phenode_backend/api/devices/routes.py:823
    sensorData: (externalDeviceId) => `/devices/${externalDeviceId}/sensor-data`,
    // GET — typed JSON diagnostics/system-health time-series for one device.
    // Same envelope + query params as `sensorData` (`from` / `to` required,
    // optional `fields`, `limit`, `bucket` incl. 'auto'). Distinct from the
    // environmental `sensorData` feed: this surfaces the Notecard's own
    // telemetry written on each `_session.qo`.
    //
    // Response shape: { deviceExternalId, from, to, bucket, rows: [...] }
    //   raw      → { time, rssi, sinr, bars, rat, notecard_voltage,
    //                notecard_temp, wifi_rssi, wifi_snr, wifi_bars }
    //   bucketed → { time, rssi_min, rssi_max, rssi_avg, ... }
    // Wi-Fi fields are null unless the device actually reports them.
    //
    // Source: phenodeX/phenode_backend/api/devices/routes.py:1032
    //         phenodeX/phenode_backend/services/downloads.py:786 (DEVICE_HEALTH_FIELDS)
    healthData: (externalDeviceId) => `/devices/${externalDeviceId}/health-data`,
    // GET — paginated list of images captured by a device, newest first.
    // Required path param: external_device_id. Optional query params:
    //   page       (default 1, min 1)
    //   page_size  (default 25, min 1, max 100)
    //   from       (ISO-8601 — filter to images at/after this timestamp)
    //   to         (ISO-8601 — filter to images at/before this timestamp)
    //
    // Response shape (ImageListResponse):
    //   {
    //     images: [{ id, device_id, timestamp, latitude, longitude,
    //                filename, storage_url, has_data }],
    //     page, page_size, total
    //   }
    //
    // The list endpoint returns *metadata only* — no base64 payload — so
    // it stays cheap to page. Use `imageDetail` to pull the full
    // base64-encoded body for a single image.
    //
    // Source: phenodeX/phenode_backend/api/devices/routes.py:572
    images: (externalDeviceId) => `/devices/${externalDeviceId}/images`,
    // GET — single image with `base64encoded` payload included
    // (ImageDetail extends ImageRead with `base64encoded`). Use when the
    // carousel/lightbox needs to display a thumbnail-less image that has
    // no `storage_url`.
    // Source: phenodeX/phenode_backend/api/devices/routes.py:649
    imageDetail: (externalDeviceId, imageId) => `/devices/${externalDeviceId}/images/${imageId}`,
    // DELETE — remove a single image identified by its filename. The
    // backend requires ADMIN role (require_role('ADMIN')) — non-admin
    // callers will receive 403. URL-encode the filename so values
    // containing reserved characters survive the path segment.
    // Source: phenodeX/phenode_backend/api/devices/routes.py:795
    imageDeleteByFilename: (externalDeviceId, filename) =>
      `/devices/${externalDeviceId}/images/delete-by-filename/${encodeURIComponent(filename)}`,
    // POST — server-generated ZIP of all images captured by a device in
    // the given date range (one file per image, plus IMAGE_URLS.txt for any
    // images stored externally rather than inline base64). Always responds
    // with application/zip ("phenode_images.zip"). ISO timestamps are
    // URL-encoded for the same reason as sensorDataDownload.
    // Source: phenodeX/phenode_backend/api/devices/routes.py:1164
    imagesDownload: (externalDeviceId, fromIso, toIso) =>
      `/devices/${externalDeviceId}/images/download/${encodeURIComponent(fromIso)}/${encodeURIComponent(toIso)}`,
    // POST — push environment variables (Notehub vars) to a device.
    // Accepts arbitrary key/value pairs (DeviceEnvironmentVariablesPayload
    // has `extra: allow`). PheNode uses this to set wifi_ssid /
    // wifi_password — the device reboots and reconnects with the new
    // credentials. Source: phenodeX/phenode_backend/api/devices/routes.py:388
    environmentVariables: (externalDeviceId) => `/devices/${externalDeviceId}/environment-variables`,
    // POST — server-generated CSV download. The backend pulls the
    // user's saved `data_download_preferences` (decimal places,
    // timezone, blank/zero/hyphen handling) and applies them to the
    // file before responding. Response is text/csv when the device has
    // no linked wireless sensors, or application/zip when it does.
    // ISO timestamps are URL-encoded because path segments are sent
    // as-is by the browser and ':' / '+' chars can confuse some proxies.
    // Source: phenodeX/phenode_backend/api/devices/routes.py:909
    // `includeWirelessSensors=false` appends ?include_wireless_sensors=false so an
    // "Environmental Data" export returns ONLY the device CSV. Default true keeps
    // the URL byte-identical to before (backward compatible). FastAPI ignores the
    // extra query param until the backend declares it, so passing false is a safe
    // no-op today and becomes effective the moment the flag lands. See
    // download-bugs-diagnosis.md (Bug 1).
    sensorDataDownload: (externalDeviceId, fromIso, toIso, includeWirelessSensors = true) =>
      withTz(
        `/devices/${externalDeviceId}/sensor-data/${encodeURIComponent(fromIso)}/${encodeURIComponent(toIso)}` +
        (includeWirelessSensors ? '' : '?include_wireless_sensors=false')
      ),
    // POST — server-generated diagnostics/health CSV download for a date
    // range. Same preference-application + filename behavior as
    // sensorDataDownload, but pulls the Notecard health series.
    // Source: phenodeX/phenode_backend/api/devices/routes.py:989
    healthDataDownload: (externalDeviceId, fromIso, toIso) =>
      withTz(`/devices/${externalDeviceId}/health-data/${encodeURIComponent(fromIso)}/${encodeURIComponent(toIso)}`),
    // POST — server-generated ZIP bundling EVERYTHING for one device over a
    // date range: environmental CSV, health/diagnostics CSV, one CSV per
    // wireless sensor, and the captured images. `sensorList` is a
    // comma-separated string of external_sensor_ids; pass 'none' (or an
    // empty list) to let the backend auto-include the sensors linked to the
    // device. User's saved data_download_preferences are applied to each
    // CSV before sealing. Always responds with application/zip ("all-data.zip").
    // Source: phenodeX/phenode_backend/api/devices/routes.py:1206
    allDataDownload: (externalDeviceId, sensorList, fromIso, toIso) =>
      withTz(`/devices/${externalDeviceId}/${encodeURIComponent(sensorList)}/all-data/${encodeURIComponent(fromIso)}/${encodeURIComponent(toIso)}`)
  },
  wirelessSensors: {
    // GET — { success, sensors: WirelessSensorListItem[] } visible to current user.
    // Each item now carries summary fields (lastMeasurementAt, healthStatus,
    // batteryPercent, soilMoisture, soilTemperatureC, rssi) populated
    // server-side via a batched latest-reading query — parallel to how
    // /devices/my-devices exposes DeviceRead summary fields.
    // Source: phenodeX/phenode_backend/api/wireless_sensors/routes.py:138-190
    //         phenodeX/phenode_backend/schemas/wireless_sensors.py:70-86
    mySensors: '/wireless-sensors/my-sensors',
    // GET — { success, sensor: WirelessSensorDetail }.
    // Source: phenodeX/docs/frontend-backend-api.md:605
    detail: (externalSensorId) => `/wireless-sensors/${externalSensorId}`,
    // POST — hide the sensor from fleet cards + maps (GLOBAL flag, one-way:
    //   restore is admin-only via API.admin.wirelessSensorVisibility). Source:
    //   phenodeX/phenode_backend/api/wireless_sensors/routes.py (hide_wireless_sensor).
    hide: (externalSensorId) => `/wireless-sensors/${externalSensorId}/hide`,
    // GET — typed JSON time-series for one wireless sensor. Same query
    // params and response envelope as the device endpoint, with a
    // different field vocabulary (mVbat, temperatureBme, vwcPercent_1,
    // electricalConductivity_1, etc.) defined in services/downloads.py:
    // _WIRELESS_FIELD_KEYS.
    //
    // Response shape:
    //   { sensorExternalId, from, to, rows: [...] }
    //
    // Source: phenodeX/phenode_backend/api/wireless_sensors/routes.py:342
    sensorData: (externalSensorId) => `/wireless-sensors/${externalSensorId}/sensor-data`,
    // POST — server-generated ZIP download containing one CSV per
    // requested sensor. `sensorList` is a comma-separated string of
    // external_sensor_id values (the backend splits on ',', see
    // wireless_sensors/routes.py:522). User's saved
    // data_download_preferences are applied to each CSV before the
    // archive is sealed. Always responds with application/zip
    // (even for a single sensor — there's no single-CSV variant of
    // this endpoint). Source: phenodeX/phenode_backend/api/wireless_sensors/routes.py:498
    sensorDataDownload: (sensorList, fromIso, toIso) =>
      withTz(`/wireless-sensors/sensor-data/${encodeURIComponent(sensorList)}/${encodeURIComponent(fromIso)}/${encodeURIComponent(toIso)}`)
  },
  // ---------------------------------------------------------------------------
  // Admin panel — SUPER_ADMIN-gated control surface (User + Device management).
  //
  // Every route here is mounted under the same /api prefix as the rest of the
  // catalog (buildUrl prepends VITE_API_URL). The backend gates these with
  // require_role('ADMIN') (ADMIN or SUPER_ADMIN); the FRONTEND shows these
  // tabs to super admins only (sections/admin/admin.jsx). Account admins get
  // the panel too (routes/RequireAdminPanel.jsx) but only its Users and Settings tabs,
  // backed by accountAdmin.* below, never these.
  //
  // Verified against:
  //   phenodeX/phenode_backend/api/admin/users.py        (users.* routes)
  //   phenodeX/phenode_backend/api/admin/routes.py       (devices.* + wirelessSensors.*)
  //   phenodeX/docs/frontend-backend-api.md:1176-1535    (request/response shapes)
  admin: {
    users: {
      // GET — UserRead[] of users where is_approved=false.
      pending: '/admin/users/pending',
      // GET — UserRead[] of all users. POST — create an email/password user
      //   (body: { email, password, full_name?, role, is_approved }).
      //   Trailing slash matches the backend router (prefix '/admin/users').
      base: '/admin/users/',
      // POST — approve/reject. Body: { user_id, approved }.
      approve: '/admin/users/approve',
      // PATCH — { full_name?, account_role?, is_active? }; SUPER_ADMIN only.
      byId: (userId) => `/admin/users/${userId}`,
      // POST — email the login a "Forgot password" link (409 without SMTP,
      //   429 inside the cooldown).
      passwordReset: (userId) => `/admin/users/${userId}/password-reset`,
      // PUT — set a temporary password: { password }.
      password: (userId) => `/admin/users/${userId}/password`
    },
    // Customer accounts (SUPER_ADMIN only; not the retired ADMIN role).
    // Verified against phenodeX/phenode_backend/api/admin/accounts.py and
    // docs/frontend-backend-api.md "Admin Accounts API". All ids are numeric db ids.
    accounts: {
      // GET — AccountRead[] (parents followed by their subaccounts).
      // POST — { name, parent_id?, can_download?, can_edit_settings? }.
      base: '/admin/accounts',
      // GET / PATCH { name?, can_download?, can_edit_settings?, is_active? } /
      //   DELETE (empty accounts only).
      byId: (accountId) => `/admin/accounts/${accountId}`,
      // GET — the account's own PheNodes. POST — { device_ids, move? }.
      devices: (accountId) => `/admin/accounts/${accountId}/devices`,
      // DELETE — detach (the PheNode and its data stay).
      device: (accountId, deviceId) => `/admin/accounts/${accountId}/devices/${deviceId}`,
      // GET — sensors the account holds directly. POST — { wireless_sensor_ids, move? }.
      wirelessSensors: (accountId) => `/admin/accounts/${accountId}/wireless-sensors`,
      wirelessSensor: (accountId, sensorId) => `/admin/accounts/${accountId}/wireless-sensors/${sensorId}`,
      // GET — the account's logins. POST — create a new login here.
      users: (accountId) => `/admin/accounts/${accountId}/users`,
      // PUT — { account_role?, move? } put an existing login here.
      //   DELETE — take it out (the login stays).
      user: (accountId, userId) => `/admin/accounts/${accountId}/users/${userId}`
    },
    // GET — AdminDeviceRead[]. POST — create device (DeviceCreate body).
    devices: '/admin/devices',
    // POST — give a user access to a PheNode, or change their level
    //   (body: { user_id, access_level: 'owner' | 'viewer' }). ADDITIVE: a
    //   PheNode can be assigned to many users. DELETE — removes EVERY user's
    //   access (legacy "unassign"; the panel removes users one at a time via
    //   deviceAssignUser). Path param is the NUMERIC db device_id, not external id.
    deviceAssign: (deviceId) => `/admin/devices/${deviceId}/assign`,
    // DELETE — remove ONE user's access to a PheNode (others keep theirs).
    //   Both path params are numeric db ids. Idempotent.
    deviceAssignUser: (deviceId, userId) => `/admin/devices/${deviceId}/assign/${userId}`,
    // POST — link a virtual wireless sensor to a device
    //   (body: { wireless_sensor_id }). Numeric device_id path param.
    deviceWirelessSensors: (deviceId) => `/admin/devices/${deviceId}/wireless-sensors`,
    // DELETE — unlink a virtual wireless sensor. Both path params numeric db ids.
    deviceWirelessSensor: (deviceId, wirelessSensorId) => `/admin/devices/${deviceId}/wireless-sensors/${wirelessSensorId}`,
    // POST — atomically SET the device's primary (virtual) sensor, recording the
    //   swap in device_primary_sensor_history (ADR-0002). Body: { wireless_sensor_id }.
    //   DELETE — clear the primary (closes the open history window). Numeric device_id.
    devicePrimarySensor: (deviceId) => `/admin/devices/${deviceId}/primary-sensor`,
    // POST — connect a sensor to a PheNode via a PHYSICAL link
    //   (device_wireless_sensors). Makes it show in that PheNode's Fleet Overview /
    //   Sensor Network + bundled downloads WITHOUT making it primary (charts
    //   untouched). Body: { wireless_sensor_id }. Numeric device_id path param.
    deviceConnectedWirelessSensors: (deviceId) => `/admin/devices/${deviceId}/connected-wireless-sensors`,
    // DELETE — remove that physical link. Both path params numeric db ids.
    deviceConnectedWirelessSensor: (deviceId, wirelessSensorId) =>
      `/admin/devices/${deviceId}/connected-wireless-sensors/${wirelessSensorId}`,
    // GET — AdminWirelessSensorRead[]. POST — create a wireless sensor
    //   (body: { external_sensor_id, label?, device_id? }).
    wirelessSensors: '/admin/wireless-sensors',
    // POST — grant a user DIRECT access to a wireless sensor (body: { user_id }).
    //   Additive/many-to-many (a sensor can be granted to several users) and
    //   idempotent; never disturbs the sensor's inherited PheNode visibility.
    //   Numeric wireless_sensor_id path param. Returns updated AdminWirelessSensorRead.
    wirelessSensorAssign: (wirelessSensorId) => `/admin/wireless-sensors/${wirelessSensorId}/assign`,
    // DELETE — revoke a user's DIRECT access grant. Both path params numeric db ids.
    //   Inherited (PheNode-derived) visibility is unaffected.
    wirelessSensorAssignUser: (wirelessSensorId, userId) => `/admin/wireless-sensors/${wirelessSensorId}/assign/${userId}`,
    // PUT — set the GLOBAL dashboard-visibility flag (body: { hidden: bool }).
    //   Both directions; drives the Visibility column toggles. Numeric db ids.
    deviceVisibility: (deviceId) => `/admin/devices/${deviceId}/visibility`,
    wirelessSensorVisibility: (wirelessSensorId) => `/admin/wireless-sensors/${wirelessSensorId}/visibility`,
    // --- Wireless-node firmware (DFU over Notecard web.get + LoRa relay) ---
    // GET  — AdminFirmwareImageRead[] (metadata only, no binaries), newest
    //   first. Includes last_serve_* fields — a best-effort note of the last
    //   byte range a gateway pulled, which the Firmware tab renders as
    //   download progress.
    // POST — multipart upload { file: *.bin, target_mac }. Upload == activate:
    //   exactly one image is active at a time and gateways pull whatever is
    //   active (device-facing route: api/notehub/routes.py
    //   serve_wireless_firmware, via a Notehub proxy route). SUPER_ADMIN only.
    // Source: phenodeX/phenode_backend/api/admin/routes.py (wireless-firmware block)
    wirelessFirmware: '/admin/wireless-firmware',
    // POST — re-arm an already-uploaded image (roll back to a known-good
    //   build). Optional body { target_mac } override; omitted keeps the
    //   image's stored target. Numeric db id.
    wirelessFirmwareActivate: (imageId) => `/admin/wireless-firmware/${imageId}/activate`,
    // DELETE — remove an image. Deleting the active one leaves NO active
    //   image; the gateway's next meta poll gets 404 no_active_image, which
    //   it treats as "nothing to do".
    wirelessFirmwareImage: (imageId) => `/admin/wireless-firmware/${imageId}`
  },
  // An account admin's Users and Settings tabs: the logins and settings of its account and, for a
  // top-level account, its subaccounts. Every route 403s anyone who isn't an
  // account admin and 404s anything outside the scope.
  // Verified against phenodeX/phenode_backend/api/account_admin.py.
  accountAdmin: {
    // GET — AccountRead[]: the caller's own account first, then its subaccounts.
    accounts: '/account-admin/accounts',
    // PATCH — { name?, can_download?, can_edit_settings?, is_active? }. The
    //   caller's own account can't be switched off (400).
    account: (accountId) => `/account-admin/accounts/${accountId}`,
    // GET — AccountUserRead[] (with account_id): every login in the scope.
    users: '/account-admin/users',
    // POST — { email, full_name?, password?, account_role? }: a new, approved login.
    accountUsers: (accountId) => `/account-admin/accounts/${accountId}/users`,
    // PUT — { account_role?, move? }: move a login between scope accounts, or set its role.
    accountUser: (accountId, userId) => `/account-admin/accounts/${accountId}/users/${userId}`,
    // PATCH — { full_name?, account_role?, is_active? }.
    user: (userId) => `/account-admin/users/${userId}`,
    // POST — emails the "Forgot password" link.
    passwordReset: (userId) => `/account-admin/users/${userId}/password-reset`,
    // PUT — { password }: a temporary password; earlier sessions end.
    password: (userId) => `/account-admin/users/${userId}/password`
  },
  user: {
    // GET — used by AuthApprovalPending to detect approval.
    // 200 once approved, 403 while pending.
    devices: '/user/devices',
    // GET — { can_download, can_edit_settings, account: { id, name } | null,
    //   manages_accounts, email_enabled }: what the caller's account allows,
    //   whether this server can send email (the reset-email options), and whether the
    //   caller is an account admin (the Admin Panel's Users and Settings tabs). The API
    //   refuses the same things (403); this only lets the app hide them first.
    //   Source: phenodeX/phenode_backend/api/user/routes.py get_my_permissions
    permissions: '/user/permissions'
  },
  userPreferences: {
    // GET — UserPreferencesRead for the current user. Auto-creates a
    // preferences row with defaults if one doesn't exist yet.
    // PUT — accepts UserPreferencesUpdate; merges uiPreferences with the
    // existing row instead of replacing wholesale.
    // Source: phenodeX/phenode_backend/api/preferences/routes.py
    base: '/user-preferences'
  }
};

export default API;
